import { useState } from 'react'
import { Table, Tag, ButtonDropdown, Button } from '@bitcine/cinesend-theme'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import useStore from '../../hooks/use_store'
import { statusTypes } from '../../constants/status_colour_map'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const AssetsCollectionTable = ({ cycle, assetId, assetType, childAssets, parentAsset }) => {
  const cycleID = cycle.id
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const { createModel, deleteModel } = useStore()
  const goToAsset = (data) => navigate(`${data.id}`)

  const createAsset = () => {
    createModel.mutate(
      {
        endpoint: `cycles/${cycleID}/assets`,
        data: {
          cycle_id: cycleID,
          title: `New ${assetType?.label}`,
          asset_type: assetType.value,
          parent_asset_id: assetId,
          // Group parents may not have dates yet (they're derived from children), so fall back to the cycle's
          start_date: parentAsset?.start_date ?? cycle.start_date,
          end_date: parentAsset?.end_date ?? cycle.end_date,
        },
      },
      {
        onSuccess: (res) => {
          if (res.data.asset) {
            goToAsset(res.data.asset)
          }
        },
      }
    )
  }
  const showValidatorsResult = (data) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {data.errors > 0 && <Tag label={`Errors: ${data.errors}`} type='error' outline={true} />}
        {data.warnings > 0 && <Tag label={`Warnings: ${data.warnings}`} type='warning' outline={true} />}
        {!data.warnings && !data.errors && <Tag label='OK' type='success' outline={true} />}
      </div>
    )
  }
  return (
    <>
      <Table
        status={{
          pending: deleteModel.isLoading,
        }}
        widths={assetType.value === 'episode' ? [200, 200, 100, 220, 65] : [200, 200, 100, 65]}
        header={{
          columns: [
            { text: 'Asset Title', key: 'asset_title' },
            { text: 'Validators', key: '' },
            { text: 'Status', key: 'status' },
            ...(assetType.value === 'episode' ? [{ text: 'Pre-Roll' }] : []),
            { text: '' },
          ],
          searching: {
            searchPlaceholder: 'Enter a search term here...',
            search,
            onSearch: (term) => setSearch(term),
          },
          customElement: (
            <Button icon='add_circle_outline' onClick={() => createAsset()}>
              Create {assetType?.label}
            </Button>
          ),
        }}
        body={{
          data: childAssets,
          row: {
            compact: true,
            spaced: true,
            onClick: (event, data) => goToAsset(data),
            render: [
              (data) => data.default_title,
              (data) => showValidatorsResult(data),
              (data) => <Tag outline type={statusTypes[data?.status?.toLowerCase()]} label={data.status ?? 'N/A'} />,
              ...(assetType.value === 'episode'
                ? [
                    (data, index) => (
                      <div className='py-2 flex flex-wrap gap-2'>
                        {data.categories
                          ?.filter((category) => category.type === 'warning-slates' || category.type === 'ad-pools')
                          .map((category) => (
                            <Tag
                              key={`${category.id}-${index}`}
                              outline
                              label={
                                category.type === 'warning-slates'
                                  ? category.category_values?.[0]?.value
                                  : category.external_unique_name || MISSING_UNIQUE_NAME
                              }
                            />
                          ))}
                      </div>
                    ),
                  ]
                : []),
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Delete',
                        className: 'text-red-500',
                        onClick: () =>
                          deleteModel.mutate({
                            endpoint: `cycles/${cycleID}/assets`,
                            id: data.id,
                            onSuccess: () => {
                              queryClient.invalidateQueries(`cycles/${cycleID}/assets/${data.parent_asset_id}`)
                            },
                          }),
                      },
                    ],
                  }}
                />
              ),
            ],
          },
          empty: {
            title: `No ${assetType?.plural_label?.toLowerCase()} found!`,
            text: `Add ${assetType?.label?.toLowerCase()} to the asset to begin.`,
            icon: 'article',
          },
        }}
      />
    </>
  )
}

export default AssetsCollectionTable
